import React, { useState, useEffect, useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Paper } from '@material-ui/core';
import {
  StateEventConfig,
  StateNodeConfig,
  StateTransition
} from '@sierralabs/workflow';

import CardTitle from '../../../common/card/CardTitle';
import { useWorkflowContext } from '../../workflow/Workflow.context';
import { Task } from '../Task.interface';
import { parseTaskStatus } from '../task.helper';
import BoardItem from './BoardItem';
import BoardItemWithSubtask from './BoardItemWithSubtask';

export interface BoardColumnProps {
  title: string;
  droppableId: string;
  tasks?: Task[];
  selectedTask?: Task;
  onDrag: boolean;
  disabled?: boolean;
  loadingItemId?: number;
  onSelectTask?: (task: Task) => void;
}

const transitionStyle = {
  borderStyle: 'dashed',
  borderColor: '#8c8c8c'
};

const BoardColumn: React.FC<BoardColumnProps> = (props: BoardColumnProps) => {
  const {
    title,
    disabled,
    droppableId,
    tasks: tasksProp,
    selectedTask,
    onDrag,
    loadingItemId,
    onSelectTask
  } = props;
  const tasks: any[] = tasksProp ? tasksProp.filter((task: any) => !!task) : [];
  const [state, setState] = useState({
    disabled: false,
    onDrag: false
  });
  const { getWorkflow } = useWorkflowContext();

  const workflowConfig: StateNodeConfig | undefined = useMemo(() => {
    return getWorkflow(selectedTask)?.toJson();
  }, [selectedTask, getWorkflow]);

  const isDropDisabled: boolean = useMemo(() => {
    // get the state of selectedTask
    // if event transitions include the title, enable drop
    const { states = [] } = workflowConfig || {};
    const taskState: StateNodeConfig | undefined = states.find(
      (state: StateNodeConfig) => {
        return state.name === parseTaskStatus(selectedTask?.status || '');
      }
    );
    return !taskState?.events?.some((event: StateEventConfig) => {
      // return title === parseTaskStatus(event.transitions[0].target);
      return event.transitions.some((transition: StateTransition) => {
        return title === parseTaskStatus(transition.target);
      });
    });
  }, [title, selectedTask, workflowConfig]);

  useEffect(() => {
    setState({ ...state, onDrag });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDrag]);

  useEffect(() => {
    selectedTask && setState({ ...state, disabled: isDropDisabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask, isDropDisabled]);

  return (
    <Paper className='column'>
      <CardTitle>{title}</CardTitle>
      <Droppable
        droppableId={droppableId}
        isDropDisabled={disabled || state.disabled}
      >
        {(provided, _snapshot) => (
          <div
            className='card-container'
            ref={provided.innerRef}
            style={!state.disabled && state.onDrag ? transitionStyle : {}}
          >
            {tasks.map((task, index) => {
              const hasSubtask = task.children?.length > 0;
              return (
                <Draggable
                  key={task.id}
                  index={index}
                  draggableId={task.id.toString()}
                >
                  {(provided, snapshot) => {
                    if (hasSubtask) {
                      return (
                        <BoardItemWithSubtask
                          {...{ task, provided, snapshot, onSelectTask }}
                          isLoading={loadingItemId === task.id}
                        />
                      );
                    }
                    return (
                      <BoardItem
                        {...{ task, provided, snapshot, onSelectTask }}
                        isLoading={loadingItemId === task.id}
                      />
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </Paper>
  );
};

export default BoardColumn;
